import { DateString, PaginationSearchParams, SearchQuery, SortOrderQuery, Uuid } from '@/api/types';
import { CaseLinkType, DonorType } from '@/api/apis/case';
import { OrganType } from '@/api/apis/case/clinical-data.types';
import { TMDXFile } from '@/api/apis/files';

export enum OfferStatus {
  Presented = 'Presented',
  Accepted = 'Accepted',
  AcceptedAhead = 'AcceptedAhead',
  AcceptedForNop = 'AcceptedForNop',
  Declined = 'Declined',
  AutoRuleOut = 'AutoRuleOut',
  Erroneous = 'Erroneous',
  Pending = 'Pending',
  Closed = 'Closed',
}

export enum OfferType {
  None = 'None',
  Primary = 'Primary',
  Backup = 'Backup',
}

export enum RecipientGender {
  None = 'None',
  Male = 'Male',
  Female = 'Female',
  Unknown = 'Unknown',
  Other = 'Other',
}

export enum RecipientBloodType {
  None = 'None',
  A = 'A',
  B = 'B',
  AB = 'AB',
  O = 'O',
}

export enum OfferLaterality {
  None = 'None',
  Bilateral = 'Bilateral',
  Right = 'Right',
  Left = 'Left',
}

export type OfferMatchDetails = {
  offerType: OfferType;
  matchId: string;
  matchSeq: number;
  matchRank?: number;
};

export type OfferRecipientKidneyInfo = {
  cpra?: string;
  waitTime?: string;
  dialysisTime?: string;
  diabetic?: string;
  epts?: string;
};

export type OfferRecipientHeartInfo = {
  lvad?: boolean;
  phm?: string;
  prv?: string;
};

export type OfferRecipientLiverInfo = {
  meldPeld?: string;
  isSlkPatient?: boolean;
};

export type OfferRecipientDetails = {
  name?: string;
  medicalRecordNumber?: string;
  dateOfBirth?: DateString;
  age?: number;
  heightCm?: number;
  weightG?: number;
  bmi?: number;
  gender?: RecipientGender;
  bloodType?: RecipientBloodType;
  priorTransplant?: string;
  multivisc?: string;
  kidneyInfo?: OfferRecipientKidneyInfo;
  heartInfo?: OfferRecipientHeartInfo;
  liverInfo?: OfferRecipientLiverInfo;
};

/** Case `OrganType` plus offer-only organs, which never exist on cases */
export const OfferOrganType = {
  ...OrganType,
  Pancreas: 'Pancreas',
  KidneyPancreas: 'KidneyPancreas',
} as const;

// Enum-like const + type: a TS `enum` can't extend `OrganType`.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type OfferOrganType = (typeof OfferOrganType)[keyof typeof OfferOrganType];

export type OfferFormFields = {
  donorId: string;
  donorType: DonorType;
  organType: OfferOrganType;
  match?: OfferMatchDetails;
  recipient?: OfferRecipientDetails;
  isInpatient?: boolean | null;
  laterality?: OfferLaterality;
  matchingSizeCriteria?: string;
  isXMatchNeeded?: boolean | null;
  xMatchType?: string;
  diagnosis?: string;
  donorHospitalId?: Uuid;
  donorHospitalName?: string;
  donorHospitalAddress?: string;
  donorHospitalLogoPath?: string;
  donorHospitalDate?: DateString | null;
  donorHospitalTime?: string | null;
  donorHospitalTimeZone?: string | null;
  opoId?: Uuid;
  opoName?: string;
  opoContactId?: Uuid;
  opoSourceContactPhone?: string;
  secondaryOpoContactId?: Uuid;
  secondaryOpoContactPhone?: string;
  files?: TMDXFile[];
  links?: CaseLinkType[];
  receivingSurgeonId?: string | null;
  coordinatorId?: string | null;
  primaryOfferReviewerId?: string | null;
  isServiceWithoutCoordinator?: boolean | null;
};

export type OfferTxRepresentative = {
  customerId?: Uuid;
  userId?: Uuid;
  firstName?: string;
  lastName?: string;
  email?: string;
  primaryPhone?: string;
  group?: string;
  photoPath?: string;
};

export type OfferOpoContact = {
  id: Uuid;
  userId?: Uuid;
  firstName?: string;
  lastName?: string;
  email?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  photoPath?: string;
};

export type OfferNoteAuthor = {
  userId?: Uuid;
  firstName?: string;
  lastName?: string;
  photoPath?: string;
  email?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  enabled?: boolean;
  customerId?: Uuid;
  userTypes?: string[];
  roles?: string[];
};

export type OfferNote = {
  id: Uuid;
  message?: string;
  authorUserId: Uuid;
  author: OfferNoteAuthor;
  createdAt: DateString;
  modifiedAt?: DateString;
  isPinned: boolean;
};

export type Offer = OfferFormFields & {
  offerId: Uuid;
  txCenterId: Uuid;
  status: OfferStatus;
  unosId: string;
  txRepresentatives?: OfferTxRepresentative[];
  opoContacts?: OfferOpoContact[];
  notes?: OfferNote[];
  createdAt: DateString;
  updatedAt: DateString;
  declineReason?: string;
  // Read-only, computed on every GET, never persisted or sent on write (ADR-011).
  offerAndDonorDetailsText?: string;
};

// Tenant (TX Center) is resolved from the `X-Tenant-Id` header (set globally by the http
// client), never from the body - the OfferService contract has no `txCenterId` request field.
export type CreateOfferRequest = OfferFormFields & {
  match: OfferMatchDetails;
};

export type UpdateOfferRequest = OfferFormFields & {
  match: OfferMatchDetails;
};

export type OfferServiceAssignedCustomer = {
  customerId: Uuid;
  group: string;
};

export type OfferServiceDonorOr = {
  dateTime: DateString;
  timezone: string;
};

export type OfferServiceMatchDetails = {
  offerType: OfferType;
  matchId?: string | null;
  matchSeq: number;
  matchRank?: number | null;
};

export type OfferServiceRecipientKidneyInfo = {
  cpra?: string | null;
  waitTime?: string | null;
  dialysisTime?: string | null;
  diabetic?: string | null;
  epts?: string | null;
};

export type OfferServiceRecipientHeartInfo = {
  lvad?: boolean | null;
  phm?: string | null;
  prv?: string | null;
};

export type OfferServiceRecipientLiverInfo = {
  meldPeld?: string | null;
  isSlkPatient?: boolean | null;
};

export type OfferServiceRecipientDetails = {
  name?: string | null;
  medicalRecordNumber?: string | null;
  dateOfBirth?: string | null;
  age?: number | null;
  heightCm?: number | null;
  weightG?: number | null;
  bmi?: number | null;
  gender?: RecipientGender;
  bloodType?: RecipientBloodType;
  priorTransplant?: string | null;
  multivisc?: string | null;
  kidneyInfo?: OfferServiceRecipientKidneyInfo;
  heartInfo?: OfferServiceRecipientHeartInfo;
  liverInfo?: OfferServiceRecipientLiverInfo;
};

export type OfferServiceWriteBody = {
  donorId?: string | null;
  organType?: OfferOrganType;
  donorType?: DonorType;
  match?: OfferServiceMatchDetails;
  recipient?: OfferServiceRecipientDetails;
  isInpatient?: boolean | null;
  laterality?: OfferLaterality;
  matchingSizeCriteria?: string | null;
  isXMatchNeeded?: boolean | null;
  xMatchType?: string | null;
  diagnosis?: string | null;
  defaultTxAssignees?: OfferServiceAssignedCustomer[];
  isServiceWithoutCoordinator?: boolean | null;
  donorHospitalId?: Uuid | null;
  opoId?: Uuid | null;
  opoContacts?: Uuid[];
  donorOr?: OfferServiceDonorOr;
};

export type OfferServiceFileModel = {
  id: Uuid;
  fileName?: string | null;
  path?: string | null;
  createdAt?: DateString;
  signedUrl?: string | null;
};

export type OfferServiceLinkModel = {
  url: string;
  description?: string | null;
};

export type OfferServiceCreateBody = OfferServiceWriteBody & {
  files?: OfferServiceFileModel[];
  links?: OfferServiceLinkModel[];
};

export type OfferServiceFileResponse = {
  id: Uuid;
  fileName?: string | null;
  path?: string | null;
  createdAt: DateString;
  signedUrl?: string | null;
};

export type OfferServiceLinkResponse = {
  id: Uuid;
  url?: string | null;
  description?: string | null;
  createdAt: DateString;
};

export type OfferServiceCustomerResponse = {
  userId?: Uuid | null;
  customerId?: Uuid | null;
  group?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  photoPath?: string | null;
  primaryPhone?: string | null;
  secondaryPhone?: string | null;
  enabled: boolean;
};

export type OfferServiceHospitalLocation = {
  id: Uuid;
  country?: string | null;
  region?: string | null;
  state?: string | null;
  city?: string | null;
  addressLine?: string | null;
  postalCode?: string | null;
  timezone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type OfferServiceHospitalDetails = {
  id: Uuid;
  name?: string | null;
  code?: string | null;
  location: OfferServiceHospitalLocation;
  logoPath?: string | null;
};

export type OfferServiceOpoLocation = {
  id: Uuid;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  addressLine?: string | null;
  postalCode?: string | null;
  timezone?: string | null;
};

export type OfferServiceOpoDetails = {
  id: Uuid;
  name?: string | null;
  code?: string | null;
  location?: OfferServiceOpoLocation;
};

export type OfferServiceOpoContact = {
  id: Uuid;
  userId?: Uuid | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  primaryPhone?: string | null;
  secondaryPhone?: string | null;
  photoPath?: string | null;
};

export type OfferServiceDonorOrDateTime = {
  dateTime: DateString;
  // Request key is `timezone`, response is `timeZoneId`.
  timeZoneId?: string | null;
};

export type OfferServiceResponse = {
  id: Uuid;
  txCenterId: Uuid;
  donorId: string | null;
  organType: OfferOrganType;
  donorType: DonorType;
  status: OfferStatus;
  match?: OfferServiceMatchDetails;
  recipient?: OfferServiceRecipientDetails;
  isInpatient?: boolean | null;
  laterality?: OfferLaterality;
  matchingSizeCriteria?: string | null;
  isXMatchNeeded?: boolean | null;
  xMatchType?: string | null;
  diagnosis?: string | null;
  defaultTxAssignees?: OfferServiceCustomerResponse[];
  isServiceWithoutCoordinator?: boolean | null;
  donorHospitalDetails?: OfferServiceHospitalDetails;
  opoDetails?: OfferServiceOpoDetails;
  opoContacts?: OfferServiceOpoContact[];
  donorOrDateTime?: OfferServiceDonorOrDateTime;
  createdAt?: string | null;
  updatedAt?: string | null;
  declineReason?: string | null;
  files?: OfferServiceFileResponse[];
  links?: OfferServiceLinkResponse[];
  notes?: OfferNoteResponse[];
  offerAndDonorDetailsText?: string | null;
};

export type OfferNoteAuthorResponse = {
  userId?: Uuid | null;
  firstName?: string | null;
  lastName?: string | null;
  photoPath?: string | null;
  email?: string | null;
  phone?: string | null;
  secondaryPhone?: string | null;
  enabled?: boolean | null;
  customerId: Uuid;
  userTypes: string[];
  roles: string[];
  txCenterId: Uuid;
};

export type OfferNoteResponse = {
  id: Uuid;
  message?: string | null;
  authorUserId: Uuid;
  author: OfferNoteAuthorResponse;
  createdAt: DateString;
  modifiedAt?: DateString | null;
  isPinned: boolean;
};

export type AddOfferNoteRequest = {
  message?: string;
};

export type UpdateOfferNoteRequest = {
  message?: string;
};

export type OffersListFilters = PaginationSearchParams &
  SearchQuery &
  SortOrderQuery & {
    txCenterId: Uuid;
    /** CSV string from useFilterQuery, or `[]` when filter cleared/empty */
    statuses?: string | string[];
    /** CSV string from useFilterQuery, or `[]` when filter cleared/empty */
    opoIds?: string | string[];
    /** CSV string from useFilterQuery, or `[]` when filter cleared/empty */
    organTypes?: string | string[];
    /** CSV string from useFilterQuery, or `[]` when filter cleared/empty */
    offerTypes?: string | string[];
    createdDateFrom?: DateString;
    createdDateTo?: DateString;
  };

export type SortDirection = 'Ascending' | 'Descending';

// BE `GetOffersOrderBy` enum, per the OfferService.Api schema.
export type GetOffersOrderBy = 'CreatedAt' | 'MatchSeq' | 'MatchRank' | 'RecipientName';

export type OfferServiceListQuery = {
  pageIndex?: number;
  itemsPerPage?: number;
  donorId?: string;
  statuses?: string[];
  opoIds?: string[];
  organTypes?: string[];
  offerTypes?: string[];
  createdDateFrom?: DateString;
  createdDateTo?: DateString;
  orderBy?: GetOffersOrderBy;
  sortDirection?: SortDirection;
};

export type DeclineOfferRequest = {
  reason?: string;
};

export type OfferLinkBody = {
  url: string;
  description?: string;
};
